import React, { useCallback, useState } from 'react'
import { Modal } from '../../../components/Modal/Modal'
import { Button } from '../../../components/Button/Button'
import { type OAuth2AppCredentials, useRotateOAuth2AppSecretMutation } from '../../../store/auth/auth'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { CredentialField } from '../components/CredentialField'

type ModalStep = 'confirm' | 'loading' | 'reveal'

interface RotateSecretModalProps {
  show: boolean
  clientId: string
  onHide: () => void
}

export const RotateSecretModal: React.FC<RotateSecretModalProps> = ({ show, clientId, onHide }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [rotateSecret] = useRotateOAuth2AppSecretMutation()

  const [step, setStep] = useState<ModalStep>('confirm')
  const [credentials, setCredentials] = useState<OAuth2AppCredentials | null>(null)

  const handleClose = useCallback(() => {
    setStep('confirm')
    setCredentials(null)
    onHide()
  }, [onHide])

  const handleRotate = useCallback(async () => {
    setStep('loading')
    try {
      const result = await rotateSecret({ accountId, clientId }).unwrap()
      setCredentials(result)
      setStep('reveal')
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to rotate secret. Please try again.'] }))
      setStep('confirm')
    }
  }, [accountId, clientId, rotateSecret, dispatch])

  const confirmBody = (
    <p className='mb-0'>Make sure to update your current secret in your app before rotating. Once rotated, the old secret will stop working.</p>
  )

  const revealBody = (
    <div>
      <p className='mb-3'>
        Copy your <strong>Client Secret now</strong>. It will never be shown again.
      </p>
      {credentials?.clientSecret ? <CredentialField label='Client Secret (Shown once only)' value={credentials.clientSecret} copyable /> : null}
    </div>
  )

  const revealFooter = (
    <div className='w-100 d-flex justify-content-center'>
      <Button variant='primary' onClick={handleClose}>
        I've saved it, continue
      </Button>
    </div>
  )

  if (step === 'reveal') {
    return <Modal show={show} onHide={handleClose} header='Rotate Client Secret' body={revealBody} footer={revealFooter} />
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      header='Rotate Client Secret'
      body={confirmBody}
      showFooterActions
      footerPrimaryActionTitle='Rotate secret'
      footerSecondaryActionTitle='Cancel'
      onFooterPrimaryAction={handleRotate}
      footerPrimaryActionLoading={step === 'loading'}
      footerPrimaryActionDisabled={step === 'loading'}
    />
  )
}
